import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, KeyRound, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Mot de passe mis à jour — connectez-vous", { duration: 6000 });
      navigate("/connexion", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Lien invalide ou expiré. Redemandez un lien.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full rounded-xl bg-black/40 border border-white/10 pl-11 pr-4 py-3.5 text-sm placeholder:text-zinc-600 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] outline-none transition-[border-color,box-shadow] duration-200";

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md relative z-10"
      >
        <Link to="/" data-testid="reset-back-home" className="flex items-center gap-2.5 mb-12 w-fit">
          <span className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#00D4FF] flex items-center justify-center font-display text-[13px] font-extrabold text-[#050505]">FX</span>
          <span className="font-display text-sm tracking-tight">Factur<span className="text-[#00D4FF]">Xpress</span></span>
        </Link>

        <p className="overline-label mb-3">Récupération</p>
        <h1 className="font-display font-light tracking-tighter text-4xl leading-tight">Nouveau mot de passe.</h1>

        {!token ? (
          <div className="mt-8 rounded-2xl border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-6" data-testid="reset-invalid-token">
            <p className="text-sm text-[#FF3B30] font-medium">Lien invalide</p>
            <p className="mt-2 text-sm text-zinc-300">
              Ce lien de réinitialisation est incomplet ou a expiré. Redemandez un lien depuis la page de connexion.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="reset-form">
            <div>
              <label htmlFor="reset-password" className="overline-label block mb-2">Nouveau mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <input
                  id="reset-password"
                  data-testid="reset-password-input"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="8 caractères minimum"
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label htmlFor="reset-confirm" className="overline-label block mb-2">Confirmation</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                <input
                  id="reset-confirm"
                  data-testid="reset-confirm-input"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Répétez le mot de passe"
                  className={inputCls}
                />
              </div>
            </div>

            {error && (
              <p data-testid="reset-error" className="text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-xl px-4 py-3">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="reset-submit-button"
              disabled={submitting}
              className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-[#2563EB] px-6 py-3.5 text-sm font-semibold hover:bg-[#00D4FF] hover:text-[#050505] hover:-translate-y-0.5 transition-[transform,background-color,color] duration-200 disabled:opacity-50 disabled:hover:translate-y-0"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              Définir le nouveau mot de passe
            </button>
          </form>
        )}

        <Link to="/connexion" data-testid="reset-back-login" className="mt-8 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#00D4FF] transition-colors duration-200">
          <ArrowLeft className="h-4 w-4" /> Retour à la connexion
        </Link>
      </motion.div>
    </div>
  );
}
